import React, { useEffect } from 'react';
import { PublicLayout } from '@/components/public';
import { BRAND } from '@/config/brand';
import { SOLUTIONS_FOOTER_COLUMN } from '@/config/verticals';

import VerticalHero from './verticals/VerticalHero';
import AnswerFirstBox from './verticals/AnswerFirstBox';
import ValueBlock from './verticals/ValueBlock';
import VerticalFaq from './verticals/VerticalFaq';
import SpokeNav from './verticals/SpokeNav';
import InlineLink from './verticals/InlineLink';

const PAGE_TITLE =
  'Online Ordering & Pre-Orders for Cafes & Bakeries | Hungry Canadian';
const PAGE_DESCRIPTION =
  'Time-slot throttling, product schedulers, and 2-way inventory syncing for cafes and bakeries. Conquer the morning rush and handle custom pre-orders up to 30 days out.';

const NAV_PROPS = {
  links: [
    { href: '#morning-rush', label: 'Morning rush', testId: 'cafes-nav-morning' },
    { href: '#pre-orders', label: 'Pre-orders', testId: 'cafes-nav-preorders' },
    { href: '#customization', label: 'Customization', testId: 'cafes-nav-customization' },
    { href: '#faq', label: 'FAQ', testId: 'cafes-nav-faq' },
  ],
  primaryCta: { href: '/#lead-form-anchor', label: 'Get Started', testId: 'cafes-nav-cta' },
};

const FOOTER_PROPS = {
  tagline:
    'A Canadian-owned delivery platform that keeps more of every sale with the local businesses that feed our communities.',
  columns: [
    {
      title: 'For Restaurants',
      links: [
        { href: '/', label: 'Why Hungry Canadian', testId: 'cafes-footer-why' },
        { href: '/tools', label: 'Tools', testId: 'cafes-footer-tools' },
        { href: '/features', label: 'Features', testId: 'cafes-footer-features' },
        { href: '/integrations', label: 'Integrations', testId: 'cafes-footer-integrations' },
      ],
    },
    SOLUTIONS_FOOTER_COLUMN,
    {
      title: 'Resources',
      links: [
        { href: 'https://help.hungrycanadian.com/', label: 'Help center', external: true, testId: 'cafes-footer-help' },
        { href: `mailto:${BRAND.merchantsEmail}`, label: 'Contact merchants', external: true, testId: 'cafes-footer-contact' },
      ],
    },
  ],
  legal: [
    { href: 'https://help.hungrycanadian.com/tos/', label: 'Terms of Service', external: true, testId: 'cafes-footer-tos' },
    { href: 'https://help.hungrycanadian.com/privacypolicy/', label: 'Privacy Policy', external: true, testId: 'cafes-footer-privacy' },
  ],
};

// Cross-link href constants (SSOT for this page)
const HREF_PRICING = '/#pricing';
const HREF_INTEGRATIONS_HUB = '/integrations';
// Cross-spoke sibling links
const HREF_GROCERY = '/for-grocery-specialty';
const HREF_GHOST_KITCHENS = '/for-ghost-kitchens-popups';

// -----------------------------------------------------------------------------
// FAQ content
// -----------------------------------------------------------------------------
const CAFES_FAQ = [
  {
    id: 'advance-preorders',
    q: 'Can customers place advance pre-orders for custom bakery items?',
    a: (
      <>
        Yes. Hungry Canadian features advanced scheduling rules allowing merchants to set custom
        order windows&mdash;ranging from 2 hours up to 30 days in advance&mdash;and restrict daily
        booking slots to manage preparation limits.
      </>
    ),
    plainAnswer:
      'Yes. Hungry Canadian features advanced scheduling rules allowing merchants to set custom order windows—ranging from 2 hours up to 30 days in advance—and restrict daily booking slots to manage preparation limits.',
  },
  {
    id: 'morning-rush',
    q: 'How does Hungry Canadian prevent cafes from getting overwhelmed during morning peak hours?',
    a: (
      <>
        Cafes can restrict the number of orders accepted per time slot, set minimum lead times,
        display morning-only menus, and adjust default prep times or temporarily pause incoming
        orders.
      </>
    ),
    plainAnswer:
      'Cafes can restrict the number of orders accepted per time slot, set minimum lead times, display morning-only menus, and adjust default prep times or temporarily pause incoming orders.',
  },
];

const FAQ_SCHEMA = CAFES_FAQ.map(({ q, plainAnswer }) => ({ q, plainAnswer }));

// -----------------------------------------------------------------------------
// Value prop block content
// -----------------------------------------------------------------------------
const BLOCK_MORNING_RUSH = {
  eyebrow: 'Rush-hour control',
  title: 'Master the Morning Rush with Intelligent Order Throttling',
  blurb:
    'A single overwhelmed barista line can lose you regulars for good. Hungry Canadian gives you granular controls to protect the counter while still capturing every sale.',
  bullets: [
    {
      label: 'Time-Restricted Product Displays',
      detail:
        'Display specific menu items only during designated times (e.g., morning coffee menus available exclusively between 7:00 AM and 10:00 AM).',
    },
    {
      label: 'Capacity Throttling',
      detail:
        'Limit the total number of orders accepted per 15-minute time slot to ensure your barista line or kitchen is never overwhelmed.',
    },
    {
      label: 'Buffer Times & Auto-Accept',
      detail:
        'Set default prep times, configure buffer intervals between order slots, or toggle your store temporarily offline during extreme spikes.',
    },
  ],
};

const BLOCK_PREORDERS = {
  eyebrow: 'Pre-orders & catering',
  title: 'Flexible Advanced Pre-Ordering & Catering Windows',
  blurb: (
    <>
      Wedding cakes,{' '}
      <InlineLink href={HREF_GROCERY} testId="block-preorders-link-catering">
        catering trays
      </InlineLink>
      , holiday pies, or{' '}
      <InlineLink href={HREF_GHOST_KITCHENS} testId="block-preorders-link-popup">
        seasonal pop-up concepts
      </InlineLink>
      {' '}&mdash; high-margin custom work needs advance planning. Configure the exact
      scheduling logic your bakery already runs by hand.
    </>
  ),
  bullets: [
    {
      label: 'Force Scheduling',
      detail:
        'Mandate that high-volume or specialty catering items be ordered in advance.',
    },
    {
      label: 'Custom Day & Hour Ranges',
      detail:
        'Set minimum and maximum scheduling windows (e.g., order at least 2 hours in advance, up to 30 days out).',
    },
    {
      label: 'Order Limits',
      detail:
        'Restrict large scheduled orders to specific hours relative to opening and closing times, and enforce minimum/maximum order totals.',
    },
  ],
};

const BLOCK_CUSTOMIZATION = {
  eyebrow: 'Customization & stock',
  title: 'Granular Coffee Customization & Perishable Stock Protection',
  blurb:
    'Replicate the in-person barista experience online, and stop selling the last croissant three times in a row when someone snags it in-store.',
  bullets: [
    {
      label: 'Additive Beverage Modifiers',
      detail:
        'Replicate the in-person barista experience with intuitive options for milk alternatives, syrup shots, espresso shots, ice percentages, and foam levels—complete with custom pricing and images.',
    },
    {
      label: 'Real-Time POS Syncing',
      detail: (
        <>
          Upgrade to{' '}
          <InlineLink href={HREF_PRICING} testId="block-customization-link-pro">
            Pro Connect
          </InlineLink>{' '}
          to sync real-time stock levels with your POS, automatically removing baked goods as they
          sell out in-store.
        </>
      ),
    },
    {
      label: 'Direct Customer Chat',
      detail:
        'Resolve out-of-stock items gracefully by offering substitutes via built-in real-time customer messaging.',
    },
  ],
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function ForCafesBakeriesPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    let meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', PAGE_DESCRIPTION);

    return () => {
      document.title = prevTitle;
      if (prevDescription != null) meta.setAttribute('content', prevDescription);
    };
  }, []);

  return (
    <PublicLayout navProps={NAV_PROPS} footerProps={FOOTER_PROPS}>
      <div data-testid="for-cafes-bakeries-page">
        <VerticalHero
          eyebrow="For Cafes & Bakeries"
          h1="Online Ordering & Pre-Orders Built for Cafes & Bakeries"
          tagline={
            <>
              Conquer the morning rush and manage custom bakery orders effortlessly with time-slot
              throttling, product schedulers, and{' '}
              <InlineLink href={HREF_INTEGRATIONS_HUB} testId="hero-tagline-link-inventory">
                two-way inventory syncing
              </InlineLink>
              .
            </>
          }
          primaryCta={{
            href: '/#lead-form-anchor',
            label: 'Get Started',
            testId: 'cafes-hero-primary-cta',
          }}
          secondaryCta={{
            href: HREF_PRICING,
            label: 'See Pricing',
            testId: 'cafes-hero-secondary-cta',
          }}
        />

        <AnswerFirstBox
          title="How Hungry Canadian Optimizes Cafe & Bakery Operations"
          testId="cafes-answer-first"
        >
          Hungry Canadian provides cafes and bakeries with specialized time-slot scheduling, order
          throttling, and advance pre-order windows (up to 30 days out). Designed to handle
          morning rushes and perishable inventory, the platform offers deep modifier logic for
          coffee customization, real-time customer chat, and automated{' '}
          <InlineLink href={HREF_INTEGRATIONS_HUB} testId="answer-link-pos-sync">
            POS inventory syncing
          </InlineLink>{' '}
          to prevent overselling fresh baked goods.
        </AnswerFirstBox>

        <div id="morning-rush">
          <ValueBlock {...BLOCK_MORNING_RUSH} testId="cafes-block-morning-rush" />
        </div>
        <div id="pre-orders">
          <ValueBlock {...BLOCK_PREORDERS} testId="cafes-block-preorders" reverse />
        </div>
        <div id="customization">
          <ValueBlock {...BLOCK_CUSTOMIZATION} testId="cafes-block-customization" />
        </div>

        <VerticalFaq
          items={CAFES_FAQ}
          schema={FAQ_SCHEMA}
          testId="cafes-faq"
          eyebrow="Cafe & Bakery FAQ"
          description="Answers to the scheduling, throttling, and pre-order questions cafes and bakeries ask most before switching platforms."
        />

        <SpokeNav currentSlug="for-cafes-bakeries" />
      </div>
    </PublicLayout>
  );
}
